'use client'

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { ArrowUp, ChevronDown, ChevronUp, Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { usePrefersReducedMotion } from './useVectorChat'

/**
 * Ask identity mark (chat bubble + spark), shared by Vector and the Readiness
 * Strategist. The inlined `<path>` below is the runtime source of truth — it is
 * rendered directly, not loaded through `next/image`, so `fill='currentColor'`
 * inherits the `text-primary` token and stays correct on both themes (same
 * reasoning as RadarMark). `public/ai-assist.svg` is the original design
 * artifact for this glyph and is NOT read at runtime; edit the path here when
 * the mark changes.
 */
function AskMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='currentColor'
      className={className}
      aria-hidden='true'
    >
      <path
        fillRule='evenodd'
        d='M12 2a10 10 0 0 1 0 20H2l2.4-4.8A9.96 9.96 0 0 1 2 12 10 10 0 0 1 12 2zm0 4.5l1.3 4.2 4.2 1.3-4.2 1.3-1.3 4.2-1.3-4.2-4.2-1.3 4.2-1.3z'
      />
    </svg>
  )
}

export type AskShellMode =
  | 'initial'
  | 'suggestions'
  | 'answer'
  | 'docked'
  | 'minimized'

export interface AskShellHandle {
  /** Pill-click semantics: answer state if a conversation exists, else suggestions. */
  restore: () => void
  /** Jump straight to the answer state (external triggers that auto-send). */
  openAnswer: () => void
  minimize: () => void
}

/**
 * Restrained typewriter over a set of example questions for the initial-state
 * placeholder. Static (first question) under reduced motion. Shared by Vector
 * and the Readiness Strategist so the looping text behaves identically.
 */
export function useRotatingPlaceholder(
  questions: string[],
  active: boolean,
  reduced: boolean
): string {
  const [text, setText] = useState(questions[0] ?? '')
  const indexRef = useRef(0)

  useEffect(() => {
    if (!active || questions.length === 0) return
    if (reduced) {
      setText(questions[0])
      return
    }
    let charCount = 0
    let holdUntil = 0
    setText('')
    const tick = setInterval(() => {
      const question = questions[indexRef.current % questions.length]
      if (charCount < question.length) {
        charCount += 1
        setText(question.slice(0, charCount))
      } else if (holdUntil === 0) {
        holdUntil = Date.now() + 2400
      } else if (Date.now() >= holdUntil) {
        indexRef.current += 1
        charCount = 0
        holdUntil = 0
        setText('')
      }
    }, 38)
    return () => clearInterval(tick)
  }, [active, reduced, questions])

  return text
}

/** Delays `true` until two frames after `active` flips on, so CSS transitions run. */
function useEntered(active: boolean): boolean {
  const [entered, setEntered] = useState(false)
  useEffect(() => {
    if (!active) {
      setEntered(false)
      return
    }
    let raf2 = 0
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setEntered(true))
    })
    return () => {
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
    }
  }, [active])
  return entered
}

/** Shared motion constants for the on-load entrance and the pill morph. */
const INTRO_DURATION_MS = 480
const MORPH_DURATION_MS = 420
const PREMIUM_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)'

/** useLayoutEffect that is inert during SSR (the shell server-renders null). */
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect

interface MorphBox {
  top: number
  left: number
  width: number
  height: number
  radius: number
  /** CSS color the surface shows at this end of the morph. */
  bg: string
}

interface MorphState {
  kind: 'minimize' | 'restore'
  /** Minimize started from the answer panel — keeps its backdrop fading out. */
  fromAnswer: boolean
  from: MorphBox
  /** Measured from the destination element right after it mounts. */
  to: MorphBox | null
}

function morphBoxOf(el: HTMLElement, radius: number, bg: string): MorphBox {
  const rect = el.getBoundingClientRect()
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
    radius,
    bg,
  }
}

/**
 * The traveling surface for minimize/restore: one fixed element that carries
 * the identity icon (and pill label) between the shell rect and the pill rect,
 * so the pill reads as the same interface in a compact state rather than a
 * crossfade between two unrelated elements. Position and size interpolate here
 * on purpose — a transform scale would distort the border, radius, and icon;
 * one small fixed element for ~420ms is the deliberate tradeoff for an
 * undistorted morph.
 */
function MorphGhost({
  kind,
  from,
  to,
  title,
  zClass,
  onDone,
}: {
  kind: 'minimize' | 'restore'
  from: MorphBox
  to: MorphBox
  title: string
  zClass: string
  onDone: () => void
}) {
  const entered = useEntered(true)
  const at = entered ? to : from
  const onDoneRef = useRef(onDone)
  useEffect(() => {
    onDoneRef.current = onDone
  })
  useEffect(() => {
    const t = setTimeout(() => onDoneRef.current(), MORPH_DURATION_MS + 80)
    return () => clearTimeout(t)
  }, [])
  // Minimizing, the label fades in as the pill forms; restoring, it fades
  // out quickly while the icon stays visible for the whole trip.
  const labelVisible = kind === 'minimize' ? entered : !entered
  return (
    <div
      aria-hidden='true'
      className={cn(
        'pointer-events-none fixed overflow-hidden border border-border',
        'shadow-[0_8px_32px_rgba(0,0,0,0.28)]',
        zClass
      )}
      style={{
        top: at.top,
        left: at.left,
        width: at.width,
        height: at.height,
        borderRadius: Math.min(at.radius, at.height / 2),
        backgroundColor: at.bg,
        transition: [
          'top',
          'left',
          'width',
          'height',
          'border-radius',
          'background-color',
        ]
          .map(p => `${p} ${MORPH_DURATION_MS}ms ${PREMIUM_EASE}`)
          .join(', '),
      }}
    >
      {/* Row mirrors the pill's content so the end states hand off seamlessly */}
      <span className='flex items-center gap-2 py-2.5 pl-3 pr-4'>
        <AskMark className='h-4 w-4 shrink-0 text-primary' />
        <span
          className={cn(
            'whitespace-nowrap text-sm font-medium leading-none text-foreground',
            'transition-opacity',
            kind === 'minimize' ? 'duration-200 delay-[200ms]' : 'duration-150',
            labelVisible ? 'opacity-100' : 'opacity-0'
          )}
        >
          {title}
        </span>
      </span>
    </div>
  )
}

/**
 * State of the cookie consent banner, read from the live DOM.
 *
 * `inset` is the extra bottom offset while the banner is on screen (its
 * measured height plus a 16px gap, 0 once consent is resolved). `visible`
 * says whether the banner is currently painted — the minimized launcher
 * stays unmounted while it is, so the two overlays never stack over the
 * hero CTA on a first mobile visit.
 *
 * CookieConsent announces visibility changes via `pm-consent-banner-change`
 * and marks its root with `data-consent-banner` / `data-visible`.
 */
function useConsentBanner(): { inset: number; visible: boolean } {
  const [state, setState] = useState({ inset: 0, visible: false })
  useEffect(() => {
    let raf = 0
    const measure = () => {
      cancelAnimationFrame(raf)
      // Measure after paint so the banner's final laid-out height is read
      raf = requestAnimationFrame(() => {
        const banner = document.querySelector<HTMLElement>(
          '[data-consent-banner][data-visible="true"]'
        )
        setState({
          inset: banner ? banner.offsetHeight + 16 : 0,
          visible: Boolean(banner),
        })
      })
    }
    measure()
    window.addEventListener('pm-consent-banner-change', measure)
    // Banner height changes with viewport width (stacked layout on mobile)
    window.addEventListener('resize', measure)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('pm-consent-banner-change', measure)
      window.removeEventListener('resize', measure)
    }
  }, [])
  return state
}

/** Lifts the bottom-anchored bar above the mobile software keyboard. */
function useKeyboardInset(enabled: boolean): number {
  const [inset, setInset] = useState(0)
  useEffect(() => {
    if (!enabled || !window.visualViewport) {
      setInset(0)
      return
    }
    const vv = window.visualViewport
    const update = () => {
      setInset(Math.max(0, window.innerHeight - vv.height - vv.offsetTop))
    }
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
      setInset(0)
    }
  }, [enabled])
  return inset
}

export interface AskShellProps {
  isVisible: boolean
  /** Header + pill identity, e.g. 'Ask Vector' / 'Ask the Strategist'. */
  title: string
  /** Muted text next to the title in the answer header. */
  subtitle?: string
  /** Extra header chips (e.g. the Vector trivia tag). */
  headerExtras?: ReactNode
  /** Placeholder shown in the initial-state bar (parent may rotate it). */
  initialPlaceholder: string
  /** Placeholder once the suggestions state is open. */
  suggestionsPlaceholder: string
  /** Placeholder for the answer-state top field. */
  answerPlaceholder: string
  /** aria-label for both search inputs. */
  inputAriaLabel: string
  suggestions: string[]
  /** Rendered above the suggestion list (intro copy, mode toggles). */
  suggestionsHeader?: ReactNode
  /** Gate for submission; when false, submits are ignored and the button disables. */
  canSubmit: boolean
  /** Disables the text inputs entirely (limit reached / gated). */
  inputDisabled?: boolean
  maxLength?: number
  onSubmit: (question: string) => void
  /** Mirrors the internal draft so parents can render char-count hints. */
  onDraftChange?: (draft: string) => void
  /** Rendered under the answer-state form (cooldown / validation hints). */
  answerInputHint?: ReactNode
  /** Determines what restore() opens. */
  hasConversation: boolean
  /** sessionStorage key persisting the user-minimized flag. */
  storageKey: string
  /** Start minimized (parent computes, including any legacy keys). */
  initiallyMinimized?: boolean
  onModeChange?: (mode: AskShellMode) => void
  /** Fired once, the first time the user opens suggestions/answer. */
  onFirstOpen?: () => void
  /** Fired once the on-load entrance has completed (or was skipped). Parents
      that rotate the initial placeholder should start rotating it then. */
  onIntroComplete?: () => void
  /** Cmd/Ctrl+K toggle (Vector yes, platform surfaces usually no). */
  enableCmdK?: boolean
  /** Applied to the visible root of each state (tour targeting). */
  dataTour?: string
  /** Stacking overrides — the platform advisor must stay at z-50 so the
      page's upgrade modal (same z, later in DOM) stacks above it. */
  overlayZClass?: string
  backdropZClass?: string
  /** Answer-panel footer (disclaimer / powered-by / limit CTA). */
  answerFooter?: ReactNode
  /** Change to auto-scroll the answer stream (when reader is near bottom). */
  scrollKey?: unknown
  /** The answer stream content. */
  children: ReactNode
}

/**
 * AskShell — the single shared presentation shell for the progressive AI
 * search interface (initial / suggestions / answer / minimized). Owns layout,
 * positioning, widths, transitions, focus management, keyboard handling,
 * scroll behavior, and accessibility. Vector (AskPixelmojo) and the Readiness
 * Strategist (AdvisorChat) both render through this component so the two
 * experiences cannot drift; parents supply only content and business logic.
 */
const AskShell = forwardRef<AskShellHandle, AskShellProps>(function AskShell(
  {
    isVisible,
    title,
    subtitle,
    headerExtras,
    initialPlaceholder,
    suggestionsPlaceholder,
    answerPlaceholder,
    inputAriaLabel,
    suggestions,
    suggestionsHeader,
    canSubmit,
    inputDisabled = false,
    maxLength = 2000,
    onSubmit,
    onDraftChange,
    answerInputHint,
    hasConversation,
    storageKey,
    initiallyMinimized = false,
    onModeChange,
    onFirstOpen,
    onIntroComplete,
    enableCmdK = false,
    dataTour,
    overlayZClass = 'z-[70]',
    backdropZClass = 'z-[60]',
    answerFooter,
    scrollKey,
    children,
  },
  ref
) {
  const [mode, setMode] = useState<AskShellMode>(() => {
    if (!initiallyMinimized) return 'initial'
    // The storage key distinguishes the wide docked bar ('docked') from the
    // bottom-right pill ('true'); parents only report the boolean.
    return typeof window !== 'undefined' &&
      sessionStorage.getItem(storageKey) === 'docked'
      ? 'docked'
      : 'minimized'
  })
  const [draft, setDraftState] = useState('')
  // Render nothing until hydrated: the initial mode comes from sessionStorage,
  // which the server can't see — emitting the bar in server HTML would
  // mismatch for users who previously minimized.
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => {
    setHydrated(true)
  }, [])

  const prefersReducedMotion = usePrefersReducedMotion()

  const wrapperRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const barFormRef = useRef<HTMLFormElement>(null)
  const bottomInputRef = useRef<HTMLInputElement>(null)
  const topInputRef = useRef<HTMLInputElement>(null)
  const pillRef = useRef<HTMLButtonElement>(null)
  const dockBarRef = useRef<HTMLDivElement>(null)
  const dockButtonRef = useRef<HTMLButtonElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const endRef = useRef<HTMLDivElement>(null)
  const nearBottomRef = useRef(true)
  const firstOpenRef = useRef(false)
  const prevModeRef = useRef<AskShellMode>(mode)

  // On-load entrance applies only when the shell mounts straight into the
  // initial bar; a restore from the pill uses the morph instead.
  const introActive = useRef(!initiallyMinimized).current
  const introPlayedRef = useRef(false)
  const introTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [introDone, setIntroDone] = useState(!introActive)
  const onIntroCompleteRef = useRef(onIntroComplete)
  useEffect(() => {
    onIntroCompleteRef.current = onIntroComplete
  })

  // Minimize/restore morph between the shell and the bottom-right pill
  const [morph, setMorph] = useState<MorphState | null>(null)
  const minimizing = morph?.kind === 'minimize'
  const restoring = morph?.kind === 'restore'

  const barVisible = mode === 'initial' || mode === 'suggestions'
  const barEntered = useEntered(barVisible)
  const dockedEntered = useEntered(mode === 'docked')
  // While a restore morph travels, targets stay hidden at their resting
  // geometry; their entrances would otherwise finish unseen underneath it.
  const suggestionsEntered = useEntered(mode === 'suggestions' && !restoring)
  const answerEntered = useEntered(mode === 'answer')
  const pillEntered = useEntered(mode === 'minimized')
  const introEntered = useEntered(hydrated && isVisible && introActive)
  const introSettling = introActive && !introDone
  const keyboardInset = useKeyboardInset(barVisible)
  const { inset: consentInset, visible: consentBannerVisible } =
    useConsentBanner()
  // The launcher is withheld while the consent banner is up so the two never
  // sandwich the hero CTA on a first mobile visit (the shell boots straight
  // into `minimized` there). It is withheld ONLY for that passive first load:
  // once the visitor has driven the shell, the pill must stay mounted or
  // minimize() has no rect to morph to and no element to return focus to.
  const [shellEngaged, setShellEngaged] = useState(false)
  const launcherWithheld = consentBannerVisible && !shellEngaged

  const setDraft = useCallback(
    (value: string) => {
      setDraftState(value)
      onDraftChange?.(value)
    },
    [onDraftChange]
  )

  // Report mode to the parent (drives e.g. Vector's rotating placeholder)
  useEffect(() => {
    onModeChange?.(mode)
  }, [mode, onModeChange])

  // First-open callback (engagement tracking / session pre-warm)
  useEffect(() => {
    if (
      (mode === 'suggestions' || mode === 'answer') &&
      !firstOpenRef.current
    ) {
      firstOpenRef.current = true
      onFirstOpen?.()
    }
  }, [mode, onFirstOpen])

  // Complete the on-load entrance: the placeholder reveals (and the parent's
  // rotating prompt starts) only once the bar has settled. Reduced motion
  // completes immediately. The timer is cleared on unmount only — a dep
  // change mid-flight must not strand introDone at false.
  useEffect(() => {
    if (!hydrated || !isVisible || introPlayedRef.current) return
    introPlayedRef.current = true
    if (!introActive || prefersReducedMotion) {
      setIntroDone(true)
      onIntroCompleteRef.current?.()
      return
    }
    introTimerRef.current = setTimeout(() => {
      setIntroDone(true)
      onIntroCompleteRef.current?.()
    }, INTRO_DURATION_MS)
  }, [hydrated, isVisible, prefersReducedMotion, introActive])
  useEffect(
    () => () => {
      if (introTimerRef.current) clearTimeout(introTimerRef.current)
    },
    []
  )

  const openSuggestions = useCallback(() => {
    setMode('suggestions')
  }, [])

  const minimize = useCallback(() => {
    if (!prefersReducedMotion && mode !== 'minimized') {
      const fromAnswer = mode === 'answer'
      const source = fromAnswer
        ? panelRef.current
        : mode === 'docked'
          ? dockBarRef.current
          : barFormRef.current
      if (source) {
        setMorph({
          kind: 'minimize',
          fromAnswer,
          from: morphBoxOf(
            source,
            16,
            fromAnswer ? 'var(--background)' : 'var(--card)'
          ),
          to: null,
        })
      }
    }
    setMode('minimized')
    setShellEngaged(true)
    sessionStorage.setItem(storageKey, 'true')
  }, [mode, prefersReducedMotion, storageKey])

  /** Collapse the answer panel to the wide docked bar (width kept, height
      down to a header strip); the page underneath becomes interactive. */
  const dock = useCallback(() => {
    setMode('docked')
    setShellEngaged(true)
    sessionStorage.setItem(storageKey, 'docked')
  }, [storageKey])

  const beginRestoreMorph = useCallback(() => {
    if (prefersReducedMotion || mode !== 'minimized' || !pillRef.current) return
    const rect = pillRef.current.getBoundingClientRect()
    setMorph({
      kind: 'restore',
      fromAnswer: false,
      from: {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        radius: rect.height / 2,
        bg: 'var(--card)',
      },
      to: null,
    })
  }, [mode, prefersReducedMotion])

  const restore = useCallback(() => {
    beginRestoreMorph()
    sessionStorage.removeItem(storageKey)
    setMode(hasConversation ? 'answer' : 'suggestions')
  }, [beginRestoreMorph, storageKey, hasConversation])

  const openAnswer = useCallback(() => {
    beginRestoreMorph()
    sessionStorage.removeItem(storageKey)
    nearBottomRef.current = true
    setMode('answer')
  }, [beginRestoreMorph, storageKey])

  // Measure the morph destination right after the target mounts (pre-paint),
  // so the ghost's first painted frame already sits on the source rect. Live
  // measurement keeps the morph correct under consent-banner/keyboard insets.
  useIsomorphicLayoutEffect(() => {
    if (!morph || morph.to) return
    const toPill = morph.kind === 'minimize'
    const toAnswer = mode === 'answer'
    const target = toPill
      ? pillRef.current
      : toAnswer
        ? panelRef.current
        : barFormRef.current
    if (!target) {
      setMorph(null)
      return
    }
    const rect = target.getBoundingClientRect()
    setMorph({
      ...morph,
      to: {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        radius: toPill ? rect.height / 2 : 16,
        bg: toPill || !toAnswer ? 'var(--card)' : 'var(--background)',
      },
    })
  }, [morph, mode])

  const finishMorph = useCallback(() => setMorph(null), [])

  // The mode-change focus below lands on a still-hidden element while a morph
  // is in flight; refocus the revealed surface once the morph completes.
  const prevMorphKindRef = useRef<MorphState['kind'] | null>(null)
  useEffect(() => {
    const prev = prevMorphKindRef.current
    prevMorphKindRef.current = morph?.kind ?? null
    if (morph || !prev) return
    if (prev === 'minimize') {
      pillRef.current?.focus()
    } else if (mode === 'answer') {
      topInputRef.current?.focus()
    } else {
      bottomInputRef.current?.focus()
    }
  }, [morph, mode])

  useImperativeHandle(ref, () => ({ restore, openAnswer, minimize }), [
    restore,
    openAnswer,
    minimize,
  ])

  const submit = useCallback(
    (raw?: string) => {
      const question = (raw ?? draft).trim()
      if (!question || !canSubmit) return
      setDraft('')
      setMode('answer')
      nearBottomRef.current = true
      onSubmit(question)
    },
    [draft, canSubmit, onSubmit, setDraft]
  )

  // Single owner of Escape and (optionally) Cmd/Ctrl+K
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (mode === 'suggestions') {
          setMode('initial')
          bottomInputRef.current?.blur()
        } else if (mode === 'answer') {
          minimize()
        }
        return
      }
      if (enableCmdK && (e.metaKey || e.ctrlKey) && e.key === 'k') {
        const el = document.activeElement as HTMLElement | null
        const typingElsewhere =
          el &&
          el !== bottomInputRef.current &&
          el !== topInputRef.current &&
          (el.tagName === 'INPUT' ||
            el.tagName === 'TEXTAREA' ||
            el.getAttribute('contenteditable') === 'true')
        if (typingElsewhere) return
        e.preventDefault()
        if (mode === 'minimized' || mode === 'docked') restore()
        else if (mode === 'initial') openSuggestions()
        else minimize()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [mode, minimize, restore, openSuggestions, enableCmdK])

  // Focus follows the state machine (restore focus after minimize / reopen)
  useEffect(() => {
    const prev = prevModeRef.current
    prevModeRef.current = mode
    if (mode === prev) return
    if (mode === 'minimized') {
      pillRef.current?.focus()
      return
    }
    if (mode === 'docked') {
      dockButtonRef.current?.focus()
      return
    }
    if (mode === 'suggestions') {
      bottomInputRef.current?.focus()
      return
    }
    if (mode === 'answer') {
      const t = setTimeout(() => topInputRef.current?.focus(), 60)
      return () => clearTimeout(t)
    }
  }, [mode])

  // Lock background scroll only in the large answer state. Restore the
  // previous inline value on cleanup so a concurrent overlay's lock is not
  // clobbered (Codex review 2026-07-11).
  useEffect(() => {
    if (mode !== 'answer') return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prevOverflow
    }
  }, [mode])

  // Click outside collapses suggestions back to the plain bar
  useEffect(() => {
    if (mode !== 'suggestions') return
    const onPointerDown = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setMode('initial')
      }
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [mode])

  // Focus trap while the answer dialog is open
  useEffect(() => {
    if (mode !== 'answer') return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !panelRef.current) return
      const focusables = panelRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])'
      )
      if (focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [mode])

  // Track whether the reader is near the bottom of the answer scroll area
  useEffect(() => {
    if (mode !== 'answer') return
    const el = scrollRef.current
    if (!el) return
    const onScroll = () => {
      nearBottomRef.current =
        el.scrollHeight - el.scrollTop - el.clientHeight < 120
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [mode])

  // Follow new content only when the reader has not scrolled up
  useEffect(() => {
    if (mode !== 'answer' || !nearBottomRef.current) return
    endRef.current?.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    })
  }, [mode, scrollKey, prefersReducedMotion])

  if (!isVisible || !hydrated) return null

  const tourAttr = dataTour ? { 'data-tour': dataTour } : {}

  return (
    <>
      {/* Backdrop behind the answer panel. Page stays visible through it.
          Stays mounted through a minimize-from-answer morph and through the
          docked state so it fades out with the collapse instead of vanishing
          on the mode flip. */}
      {(mode === 'answer' ||
        mode === 'docked' ||
        (minimizing && morph?.fromAnswer)) && (
        <div
          className={cn(
            'fixed inset-0 bg-black/40 transition-opacity duration-300 motion-reduce:transition-none',
            backdropZClass,
            mode === 'answer' && answerEntered
              ? 'opacity-100'
              : 'pointer-events-none opacity-0'
          )}
          onClick={mode === 'answer' ? minimize : undefined}
          aria-hidden='true'
        />
      )}

      {/* Bottom-center morphing container: initial / suggestions / answer */}
      {mode !== 'minimized' && (
        <div
          ref={wrapperRef}
          className={cn(
            'pointer-events-none fixed inset-x-0 flex justify-center px-4',
            'transition-[bottom] duration-300 ease-out motion-reduce:transition-none',
            overlayZClass
          )}
          style={{
            bottom: `calc(env(safe-area-inset-bottom, 0px) + 1.25rem + ${consentInset}px)`,
            transform:
              keyboardInset > 0 ? `translateY(-${keyboardInset}px)` : undefined,
            // Hidden (not unmounted) during a restore morph: the target keeps
            // its resting geometry for the ghost to land on, then reveals.
            visibility: restoring ? 'hidden' : undefined,
          }}
        >
          {mode === 'answer' ? (
            <div
              ref={panelRef}
              role='dialog'
              aria-modal='true'
              aria-label={title}
              {...tourAttr}
              className={cn(
                'pointer-events-auto flex w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-border bg-background md:max-w-[min(1200px,calc(100vw-64px))]',
                'shadow-[0_24px_64px_rgba(0,0,0,0.35)]',
                'transition-all duration-300 ease-out motion-reduce:transition-none',
                // Forced to resting geometry during a restore morph so the
                // pre-paint measurement reads the true destination rect.
                answerEntered || restoring
                  ? 'translate-y-0 scale-100 opacity-100'
                  : 'translate-y-4 scale-[0.98] opacity-0'
              )}
              style={{
                // Shrink by the consent-banner inset so the lifted panel
                // never pushes its header off the top of the viewport
                height: `min(680px, calc(100dvh - 5.5rem - ${consentInset}px))`,
              }}
            >
              {/* Single coordinated content reveal after a restore morph —
                  the frame arrives via the ghost, then content fades in. */}
              <div
                className={cn(
                  'flex min-h-0 flex-1 flex-col transition-opacity duration-200 motion-reduce:transition-none',
                  restoring ? 'opacity-0' : 'opacity-100'
                )}
              >
                {/* Header */}
                <div className='flex items-center justify-between gap-3 border-b border-border px-4 py-3'>
                  <div className='flex min-w-0 items-center gap-2'>
                    <AskMark className='h-4 w-4 shrink-0 text-primary' />
                    <span className='text-sm font-semibold leading-none text-foreground'>
                      {title}
                    </span>
                    {subtitle && (
                      <span className='hidden text-xs leading-none text-muted-foreground sm:inline'>
                        {subtitle}
                      </span>
                    )}
                    {headerExtras}
                  </div>
                  <div className='flex shrink-0 items-center gap-1'>
                    <button
                      onClick={dock}
                      className='flex h-9 w-9 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
                      aria-label={`Minimize ${title}`}
                    >
                      <ChevronDown className='h-5 w-5' />
                    </button>
                    <button
                      onClick={minimize}
                      className='flex h-9 w-9 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
                      aria-label={`Close ${title}`}
                    >
                      <X className='h-5 w-5' />
                    </button>
                  </div>
                </div>

                {/* Search field at the top of the answer surface */}
                <form
                  onSubmit={e => {
                    e.preventDefault()
                    submit()
                  }}
                  className='border-b border-border px-4 py-3'
                >
                  <div className='flex h-12 items-center gap-2 rounded-xl border border-input bg-card pl-3 pr-1.5 transition-colors focus-within:border-primary/50'>
                    <Search
                      className='h-4 w-4 shrink-0 text-muted-foreground'
                      aria-hidden='true'
                    />
                    <input
                      ref={topInputRef}
                      type='text'
                      value={draft}
                      maxLength={maxLength}
                      onChange={e => setDraft(e.target.value)}
                      placeholder={answerPlaceholder}
                      disabled={inputDisabled}
                      className='min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 md:text-base'
                      aria-label={inputAriaLabel}
                    />
                    <button
                      type='submit'
                      disabled={!draft.trim() || !canSubmit}
                      className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
                      aria-label='Submit question'
                    >
                      <ArrowUp className='h-4 w-4' />
                    </button>
                  </div>
                  {answerInputHint}
                </form>

                {/* Answer stream */}
                <div
                  ref={scrollRef}
                  className='flex-1 overflow-y-auto overscroll-contain px-4 py-5 md:px-6'
                  role='log'
                  aria-live='polite'
                  aria-label='Answers'
                >
                  {/* Readable line length inside the wide panel */}
                  <div className='mx-auto w-full max-w-5xl space-y-6'>
                    {children}
                  </div>
                  <div ref={endRef} />
                </div>

                {answerFooter}
              </div>
            </div>
          ) : mode === 'docked' ? (
            /* Docked: the answer panel collapsed to a wide header strip.
               Same width as the panel; the page underneath stays usable. */
            <div
              ref={dockBarRef}
              {...tourAttr}
              className={cn(
                'pointer-events-auto flex h-14 w-full max-w-2xl items-center gap-1 rounded-2xl border border-border bg-card pr-2 md:max-w-[min(1200px,calc(100vw-64px))]',
                'shadow-[0_16px_48px_rgba(0,0,0,0.3)]',
                'transition-all duration-300 ease-out motion-reduce:transition-none',
                dockedEntered
                  ? 'translate-y-0 opacity-100'
                  : 'translate-y-4 opacity-0'
              )}
            >
              <button
                ref={dockButtonRef}
                type='button'
                onClick={restore}
                className='flex h-full min-w-0 flex-1 items-center gap-2 rounded-2xl pl-4 pr-2 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
                aria-label={`Expand ${title}`}
                aria-expanded='false'
              >
                <AskMark className='h-4 w-4 shrink-0 text-primary' />
                <span className='truncate text-sm font-semibold leading-none text-foreground'>
                  {title}
                </span>
                {subtitle && (
                  <span className='hidden truncate text-xs leading-none text-muted-foreground sm:inline'>
                    {subtitle}
                  </span>
                )}
                <ChevronUp className='ml-auto h-4 w-4 shrink-0 text-muted-foreground' />
              </button>
              <button
                type='button'
                onClick={minimize}
                className='flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
                aria-label={`Close ${title}`}
              >
                <X className='h-5 w-5' />
              </button>
            </div>
          ) : (
            <div
              {...tourAttr}
              className='pointer-events-auto flex w-full max-w-xl flex-col md:max-w-[min(1100px,calc(100vw-64px))]'
            >
              {/* Suggestions above the input */}
              {mode === 'suggestions' && (
                <div
                  role='region'
                  aria-label='Suggested questions'
                  className={cn(
                    'mb-3 rounded-2xl border border-border bg-background p-3',
                    'shadow-[0_16px_48px_rgba(0,0,0,0.3)]',
                    'transition-all duration-300 ease-out motion-reduce:transition-none',
                    suggestionsEntered
                      ? 'translate-y-0 opacity-100'
                      : 'translate-y-3 opacity-0'
                  )}
                >
                  {suggestionsHeader}
                  <ul className='flex flex-col'>
                    {suggestions.map((question, index) => (
                      <li key={question}>
                        <button
                          onClick={() => submit(question)}
                          className={cn(
                            'w-full rounded-xl px-3 py-2.5 text-left transition-all duration-300 ease-out motion-reduce:transition-none',
                            'hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                            suggestionsEntered
                              ? 'translate-y-0 opacity-100'
                              : 'translate-y-1 opacity-0'
                          )}
                          style={{
                            transitionDelay:
                              suggestionsEntered && !prefersReducedMotion
                                ? `${index * 40}ms`
                                : undefined,
                          }}
                        >
                          <span className='flex items-center gap-2.5'>
                            <Search className='h-4 w-4 shrink-0 text-muted-foreground' />
                            <span className='text-sm leading-snug text-foreground'>
                              {question}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* The search bar (initial + suggestions states) */}
              <form
                ref={barFormRef}
                onSubmit={e => {
                  e.preventDefault()
                  submit()
                }}
                className={cn(
                  'flex h-14 items-center gap-2 rounded-2xl border bg-card pl-4 pr-2 md:h-16',
                  'shadow-[0_16px_48px_rgba(0,0,0,0.3)]',
                  mode === 'suggestions'
                    ? 'border-primary/50'
                    : 'border-border',
                  // On-load entrance: rise, decompress, and sharpen in one
                  // settle — no bounce. Falls back to the standard entrance
                  // once the intro has completed.
                  introSettling
                    ? [
                        'transition-[transform,opacity,filter] duration-[480ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
                        introEntered
                          ? 'opacity-100 blur-[0px] [transform:none]'
                          : 'opacity-0 blur-[5px] [transform:translateY(18px)_scaleX(0.94)_scaleY(0.96)]',
                      ]
                    : [
                        'transition-all duration-300 ease-out motion-reduce:transition-none',
                        barEntered || restoring
                          ? 'translate-y-0 opacity-100'
                          : 'translate-y-4 opacity-0',
                      ]
                )}
              >
                {/* Single coordinated content reveal after a restore morph */}
                <div
                  className={cn(
                    'flex min-w-0 flex-1 items-center gap-2 transition-opacity duration-200 motion-reduce:transition-none',
                    restoring ? 'opacity-0' : 'opacity-100'
                  )}
                >
                  <Search
                    className={cn(
                      'h-5 w-5 shrink-0 text-muted-foreground',
                      // Short delayed scale pulse once the shell is appearing
                      introSettling && [
                        'transition-[scale,opacity] duration-300 delay-[240ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:delay-0',
                        introEntered
                          ? 'scale-100 opacity-100'
                          : 'scale-50 opacity-0',
                      ]
                    )}
                    aria-hidden='true'
                  />
                  <input
                    ref={bottomInputRef}
                    type='text'
                    value={draft}
                    maxLength={maxLength}
                    onChange={e => {
                      setDraft(e.target.value)
                      if (mode === 'initial') openSuggestions()
                    }}
                    onFocus={() => {
                      if (mode === 'initial') openSuggestions()
                    }}
                    onClick={() => {
                      if (mode === 'initial') openSuggestions()
                    }}
                    placeholder={
                      mode === 'initial'
                        ? // The rotating prompt waits for the entrance to settle
                          introDone
                          ? initialPlaceholder
                          : ''
                        : suggestionsPlaceholder
                    }
                    disabled={inputDisabled}
                    className='min-w-0 flex-1 bg-transparent text-base text-foreground outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 md:text-lg'
                    aria-label={inputAriaLabel}
                  />
                  <button
                    type='submit'
                    disabled={!draft.trim() || !canSubmit}
                    className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
                    aria-label='Submit question'
                  >
                    <ArrowUp className='h-5 w-5' />
                  </button>
                  <button
                    type='button'
                    onClick={minimize}
                    className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
                    aria-label={`Minimize ${title}`}
                  >
                    <X className='h-5 w-5' />
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {/* Minimized: persistent bottom-right pill (icon + label, never icon-only).
          Withheld while the consent banner is up so the launcher and the banner
          never sandwich the hero CTA on a first mobile visit. */}
      {mode === 'minimized' && !launcherWithheld && (
        <button
          ref={pillRef}
          onClick={restore}
          {...tourAttr}
          className={cn(
            'fixed right-4 flex min-h-11 items-center gap-2 rounded-full border border-border bg-card py-2.5 pl-3 pr-4 md:right-6',
            overlayZClass,
            'shadow-[0_4px_16px_rgba(0,0,0,0.25)]',
            'transition-all duration-300 ease-out motion-reduce:transition-none',
            'hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            // Forced to resting geometry during a minimize morph so the
            // pre-paint measurement reads the true destination rect.
            pillEntered || minimizing
              ? 'translate-y-0 opacity-100'
              : 'translate-y-4 opacity-0'
          )}
          style={{
            bottom: `calc(env(safe-area-inset-bottom, 0px) + 1rem + ${consentInset}px)`,
            // Hidden (not unmounted) while the ghost travels toward it, then
            // revealed in place — visibility flips without a fade.
            visibility: minimizing ? 'hidden' : undefined,
          }}
          aria-label={`Open ${title}`}
        >
          <AskMark className='h-4 w-4 text-primary' />
          <span className='text-sm font-medium leading-none text-foreground'>
            {title}
          </span>
        </button>
      )}

      {/* Traveling morph surface between the shell and the pill */}
      {morph && morph.to && (
        <MorphGhost
          key={`${morph.kind}-${Math.round(morph.from.top)}-${Math.round(morph.from.left)}-${Math.round(morph.from.width)}`}
          kind={morph.kind}
          from={morph.from}
          to={morph.to}
          title={title}
          zClass={overlayZClass}
          onDone={finishMorph}
        />
      )}
    </>
  )
})

export default AskShell
